import React, { useState, useEffect } from "react";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { ConfirmationModalBase } from "@/components/common/modals";
import FullModal from "../../full-modal";
import { InputOutput } from "../../InputOutput";
import { DataCenterNode } from "./data-center-node";
import { DataCenterNodeProvider, useDataCenterNode } from "./context";
import { Icon } from "@elaniintc/goes-design-system";
import Variables from "@/workflow_api/components/variables";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { NodeTypes, type NodeData } from "@/workflow_api/types/workflow";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";

export const DataCenterDetailsForm: React.FC = () => {
  const {
    nodeDetailsOpen,
    setNodeDetailsOpen,
    setSelectedNodeId,
    selectedNodeId,
  } = useUIStore();
  const { getNodeById } = useWorkflowStore();
  const node = getNodeById(selectedNodeId || "");
  const nodeData = node?.data as unknown as NodeData;
  const [title, setTitle] = useState<string>(
    nodeData.displayName || "Centro de datos",
  );
  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );
  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);
  const { getNameValidationError, isNameValid } = useNodeNameValidation(selectedNodeId);
  const [manualConfirmation, setManualConfirmation] = useState(() => {
    return nodeData?.manual_continue ?? false;
  });

  // Actualizar manualConfirmation cuando nodeData cambie
  useEffect(() => {
    if (nodeData?.manual_continue !== undefined) {
      setManualConfirmation(nodeData.manual_continue);
    }
  }, [nodeData?.manual_continue]);

  const {
    isValidScope,
    variableName,
    isVariableNameValid,
    conditionsAreValid,
    selectedAction,
    selectedTable,
    isReadyToShow,
    columnsAreValid,
    saveNode,
  } = useDataCenterNode();

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={false}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId();
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <FullModal
        isOpen={nodeDetailsOpen}
        isSaveValid={
          isReadyToShow &&
          isValidScope &&
          !!selectedTable &&
          !!selectedAction &&
          !!variableName &&
          isVariableNameValid &&
          (["read", "update", "delete"].includes(selectedAction.id)
            ? conditionsAreValid
            : true) &&
          (["create", "update"].includes(selectedAction.id)
            ? columnsAreValid
            : true) &&
          isNameValid(title)
        }
        onClose={() => {
          setModalState("close-warning");
        }}
        onSave={() => {
          setShowNodeTitleValidation(true);
          if (!isNameValid(title)) return;
          saveNode(title, manualConfirmation);
          setNodeDetailsOpen(false);
          setSelectedNodeId();
        }}
      >
        <ThreeColumnLayout
          className="overflow-auto"
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
            hideSearch: true,
          }}
          centerHeader={{
            icon: "table",
            title: title,
            color: "info",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setTitle(newTitle);
              if (newTitle.trim() === "") {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => setShowNodeTitleValidation(true),
            validationError: getNameValidationError(title),
            showValidation: showNodeTitleValidation,
            manualConfirmation,
            onManualConfirmationChange: setManualConfirmation,
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
                <Variables sourceNodeId={selectedNodeId!} />
              </div>
            </>
          }
          leftContentWidth="min-w-[445px]"
          centerContent={
            isReadyToShow ? (
              <DataCenterNode />
            ) : (
              <div className="min-h-[48px] text-body-loud font-semibold flex justify-center items-center py-0.5 px-3 gap-4 ">
                <Icon
                  type="far"
                  name="spinner-third"
                  className="animate-spin text-xl text-body"
                />
              </div>
            )
          }
          rightContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
                <InputOutput
                  nodeType={NodeTypes.DATA_CENTER}
                  showSteps={false}
                  nodeLabel={title}
                  variableName={variableName}
                  isLoading={!isReadyToShow}
                  params={{
                    operation: selectedAction?.id,
                  }}
                />
              </div>
            </>
          }
        />
      </FullModal>
    </>
  );
};

export const DataCenterDetails: React.FC = () => {
  return (
    <DataCenterNodeProvider>
      <DataCenterDetailsForm />
    </DataCenterNodeProvider>
  );
};
